import {Rental} from '../../../../common/types/rental'
import {getSharesInPercentage} from '../../utils/getSharesInPercentage'

export type ProductRentalStatsRow = {
  productId: string
  productName: string

  rentalsCount: number

  // “Active boxes”: boxes that actually rented this product at least once in the period.
  boxesActiveCount: number

  // rentalsCount / boxesActiveCount (0 when boxesActiveCount=0)
  rentalsPerActiveBox: number

  // Share of counted rentals (0–100)
  rentalShare: number

  // Mean rating based on rentals with review.rating
  meanRating: number
  ratingsSum: number
  ratingsCount: number

  // Mean rent time based on rentals with rentTime
  meanRentTime: number
  rentTimeSum: number
  rentTimeCount: number
}

type InternalAccumulator = {
  productId: string
  productName: string

  rentalsCount: number

  activeBoxIds: Set<string>

  ratingsSum: number
  ratingsCount: number

  rentTimeSum: number
  rentTimeCount: number
}

export const computeProductRentalStatsRows = (rentals: Rental[]): ProductRentalStatsRow[] => {
  const byProduct: Record<string, InternalAccumulator> = {}

  for (const rental of rentals) {
    const product = rental.product
    if (!product?.id) continue

    const productId = product.id
    const productName = product.name ?? '(Unknown product)'

    if (!byProduct[productId]) {
      byProduct[productId] = {
        productId,
        productName,
        rentalsCount: 0,
        activeBoxIds: new Set<string>(),
        ratingsSum: 0,
        ratingsCount: 0,
        rentTimeSum: 0,
        rentTimeCount: 0,
      }
    }

    const acc = byProduct[productId]
    acc.rentalsCount++

    const boxId = rental.box?.id
    if (boxId) acc.activeBoxIds.add(boxId)

    const rating = rental.review?.rating
    if (typeof rating === 'number') {
      acc.ratingsSum += rating
      acc.ratingsCount += 1
    }

    const rentTime = rental.rentTime
    if (typeof rentTime === 'number' && rentTime > 0) {
      acc.rentTimeSum += rentTime
      acc.rentTimeCount += 1
    }
  }

  const rows = Object.values(byProduct).map((acc): ProductRentalStatsRow => {
    const boxesActiveCount = acc.activeBoxIds.size
    const rentalsPerActiveBox = boxesActiveCount > 0 ? acc.rentalsCount / boxesActiveCount : 0

    const meanRating = acc.ratingsCount > 0 ? acc.ratingsSum / acc.ratingsCount : 0

    const meanRentTime = acc.rentTimeCount > 0 ? acc.rentTimeSum / acc.rentTimeCount : 0

    return {
      productId: acc.productId,
      productName: acc.productName,

      rentalsCount: acc.rentalsCount,
      boxesActiveCount,
      rentalsPerActiveBox,

      rentalShare: 0, // filled below
      meanRating,
      ratingsSum: acc.ratingsSum,
      ratingsCount: acc.ratingsCount,

      meanRentTime,
      rentTimeSum: acc.rentTimeSum,
      rentTimeCount: acc.rentTimeCount,
    }
  })

  // Compute shares using the total *counted* rentals (i.e. rentals with product id).
  const totalCountedRentals = rows.reduce((sum, r) => sum + r.rentalsCount, 0)

  rows.forEach((r) => {
    r.rentalShare = getSharesInPercentage(r.rentalsCount, totalCountedRentals)
  })

  // Sort by rentals descending (table default)
  rows.sort((a, b) => b.rentalsCount - a.rentalsCount)

  return rows
}
